/**
 * UploadQueue: drives the recorded -> queued -> uploading(partN) -> completing ->
 * done | failed(retryable) state machine for recording uploads.
 *
 * Design notes:
 * - Persistence and actual byte transfer are both pluggable (Storage / Uploader), so
 *   web and native supply platform specifics (IndexedDB vs SQLite, fetch/XHR vs
 *   native multipart PUT) while this class owns the state machine + retry policy.
 * - `resume()` reloads all items from Storage and continues driving any that were
 *   left mid-flight (queued/uploading/completing) when the process last stopped —
 *   this is how a killed app / closed tab picks back up.
 * - The driver only ever marks an item `done` after `uploader.completeUpload()`
 *   resolves successfully; a crash between the last part PUT and the complete call
 *   simply resumes at `completing` since `completedParts` already has every part.
 * - Concurrency: `start()` drives all eligible items concurrently (one in-flight
 *   network op per item), each with its own retry/backoff timer.
 */
import { computeBackoffDelay, resolveBackoff } from "./backoff.js";
import { defaultClassifyError } from "./errors.js";
function nowIso(clock) {
    return new Date(clock()).toISOString();
}
function generateId() {
    // Prefer platform crypto.randomUUID when available (Node 19+, browsers, RN with polyfill);
    // fall back to a non-cryptographic id for older/edge environments (test doubles etc).
    const g = globalThis;
    if (g.crypto?.randomUUID)
        return g.crypto.randomUUID();
    return `up_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
export class UploadQueue {
    storage;
    uploader;
    classifyError;
    clock;
    setTimeoutImpl;
    clearTimeoutImpl;
    backoffOptions;
    items = new Map();
    timers = new Map();
    inFlight = new Set();
    listeners = new Set();
    stopped = false;
    constructor(options) {
        this.storage = options.storage;
        this.uploader = options.uploader;
        this.classifyError = options.classifyError ?? defaultClassifyError;
        this.clock = options.now ?? Date.now;
        this.setTimeoutImpl = options.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
        this.clearTimeoutImpl = options.clearTimeout ?? ((h) => clearTimeout(h));
        this.backoffOptions = options.backoff;
    }
    // ── observation ────────────────────────────────────────────────────────────────
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    emit(event) {
        for (const listener of this.listeners)
            listener(event);
    }
    list() {
        return [...this.items.values()];
    }
    get(id) {
        return this.items.get(id);
    }
    // ── lifecycle ──────────────────────────────────────────────────────────────────
    /** Loads persisted items and resumes driving any that weren't terminal. */
    async resume() {
        this.stopped = false;
        const persisted = await this.storage.loadAll();
        for (const item of persisted) {
            this.items.set(item.id, item);
        }
        for (const item of persisted) {
            if (item.state.kind !== "done" && item.state.kind !== "failed") {
                this.schedule(item.id, 0);
            }
            else if (item.state.kind === "failed" && item.state.retryable) {
                // Retryable failures are not auto-scheduled on resume (avoid surprise network
                // activity right after app start); caller can call retry() explicitly. We still
                // load them so `list()` reflects reality.
            }
        }
    }
    /** Stops scheduling further work. In-flight network calls are not aborted. */
    stop() {
        this.stopped = true;
        for (const handle of this.timers.values())
            this.clearTimeoutImpl(handle);
        this.timers.clear();
    }
    // ── enqueue ────────────────────────────────────────────────────────────────────
    /**
     * Registers a newly recorded local file with the queue in the `recorded` state.
     * Call `attachUpload()` once POST /recordings has returned the PresignedUpload,
     * then the driver will move it through `queued` automatically. This split exists
     * because obtaining the PresignedUpload is an API call the caller (not this
     * package's client-agnostic FSM) is responsible for making, typically via
     * `ScribeApiClient.createRecording()`.
     */
    async addRecorded(source, id = generateId()) {
        const ts = nowIso(this.clock);
        const item = {
            id,
            source,
            state: { kind: "recorded" },
            completedParts: [],
            attempt: 0,
            totalFailures: 0,
            createdAt: ts,
            updatedAt: ts,
        };
        await this.persist(item);
        this.emit({ type: "item-added", item });
        return item;
    }
    /**
     * Attaches the server-issued recording id + presigned upload plan to a `recorded`
     * item and transitions it to `queued`, kicking off the drive loop.
     */
    async attachUpload(id, recordingId, upload) {
        const item = this.require(id);
        if (upload.parts.length !== item.source.partCount) {
            throw new Error(`attachUpload: presigned part count (${upload.parts.length}) does not match source.partCount (${item.source.partCount}) for item ${id}`);
        }
        const updated = {
            ...item,
            recordingId,
            upload,
            state: { kind: "queued" },
            attempt: 0,
            updatedAt: nowIso(this.clock),
        };
        await this.persist(updated);
        this.schedule(id, 0);
        return updated;
    }
    /** Directly enqueue an item that already has its recording + upload plan (convenience). */
    async enqueue(source, recordingId, upload, id = generateId()) {
        await this.addRecorded(source, id);
        return this.attachUpload(id, recordingId, upload);
    }
    // ── manual controls ────────────────────────────────────────────────────────────
    /** Re-drives a failed (or stuck) item immediately, resetting its attempt counter. */
    async retry(id) {
        const item = this.require(id);
        if (item.state.kind === "done")
            return;
        const nextState = item.completedParts.length === item.source.partCount
            ? { kind: "completing" }
            : { kind: "queued" };
        await this.transition(id, nextState, { resetAttempt: true });
        this.schedule(id, 0);
    }
    /** Marks an item permanently failed and stops retrying it (user-initiated cancel). */
    async cancel(id, reason = "cancelled_by_user") {
        const item = this.require(id);
        this.clearTimer(id);
        await this.transition(id, { kind: "failed", retryable: false, reason });
        if (this.uploader.abortUpload && item.upload) {
            try {
                await this.uploader.abortUpload({ item, abortUrl: item.upload.abort_url });
            }
            catch {
                // Best-effort; the item is already marked failed regardless.
            }
        }
    }
    async remove(id) {
        this.clearTimer(id);
        this.items.delete(id);
        await this.storage.remove(id);
        this.emit({ type: "item-removed", id });
    }
    // ── driving ────────────────────────────────────────────────────────────────────
    schedule(id, delayMs) {
        if (this.stopped)
            return;
        this.clearTimer(id);
        const handle = this.setTimeoutImpl(() => {
            this.timers.delete(id);
            void this.drive(id);
        }, delayMs);
        this.timers.set(id, handle);
    }
    clearTimer(id) {
        const handle = this.timers.get(id);
        if (handle !== undefined) {
            this.clearTimeoutImpl(handle);
            this.timers.delete(id);
        }
    }
    async drive(id) {
        if (this.stopped || this.inFlight.has(id))
            return;
        const item = this.items.get(id);
        if (!item)
            return;
        this.inFlight.add(id);
        try {
            switch (item.state.kind) {
                case "queued":
                    await this.driveNextPart(item);
                    break;
                case "uploading":
                    await this.driveNextPart(item);
                    break;
                case "completing":
                    await this.driveComplete(item);
                    break;
                case "recorded":
                case "done":
                case "failed":
                    // Nothing to drive automatically.
                    break;
            }
        }
        finally {
            this.inFlight.delete(id);
        }
    }
    async driveNextPart(item) {
        if (!item.upload || !item.recordingId) {
            await this.transition(item.id, {
                kind: "failed",
                retryable: false,
                reason: "missing_upload_plan",
            });
            return;
        }
        const nextPartNumber = item.completedParts.length + 1;
        if (nextPartNumber > item.source.partCount) {
            // All parts already uploaded (e.g. resumed after a crash right before completing).
            await this.transition(item.id, { kind: "completing" });
            this.schedule(item.id, 0);
            return;
        }
        const partSpec = item.upload.parts.find((p) => p.part_number === nextPartNumber);
        if (!partSpec) {
            await this.transition(item.id, {
                kind: "failed",
                retryable: false,
                reason: `no_presigned_url_for_part_${nextPartNumber}`,
            });
            return;
        }
        await this.transition(item.id, { kind: "uploading", partNumber: nextPartNumber });
        const current = this.require(item.id);
        try {
            const etag = await this.uploader.uploadPart({
                item: current,
                partNumber: nextPartNumber,
                url: partSpec.url,
            });
            const completedParts = [
                ...current.completedParts,
                { partNumber: nextPartNumber, etag },
            ];
            const isLastPart = completedParts.length === current.source.partCount;
            const updated = {
                ...current,
                completedParts,
                state: isLastPart ? { kind: "completing" } : { kind: "queued" },
                attempt: 0,
                updatedAt: nowIso(this.clock),
            };
            await this.persist(updated);
            this.schedule(item.id, 0);
        }
        catch (error) {
            await this.handleFailure(item.id, error);
        }
    }
    async driveComplete(item) {
        if (!item.upload) {
            await this.transition(item.id, {
                kind: "failed",
                retryable: false,
                reason: "missing_upload_plan",
            });
            return;
        }
        try {
            await this.uploader.completeUpload({
                item,
                completeUrl: item.upload.complete_url,
                parts: item.completedParts,
            });
            // Only mark done after the complete call has actually succeeded.
            await this.transition(item.id, { kind: "done", etags: item.completedParts });
        }
        catch (error) {
            await this.handleFailure(item.id, error);
        }
    }
    async handleFailure(id, error) {
        const item = this.require(id);
        const { retryable, reason } = this.classifyError(error);
        const attempt = item.attempt + 1;
        const backoff = resolveBackoff(this.backoffOptions);
        const updated = {
            ...item,
            attempt,
            totalFailures: item.totalFailures + 1,
            updatedAt: nowIso(this.clock),
        };
        if (!retryable) {
            // Permanent failure (e.g. expired presigned URL, malformed request): no amount
            // of automatic retrying will help. `retryable: false` here means "don't
            // auto-retry"; a caller can still force a manual retry() after fixing the
            // underlying cause (e.g. re-issuing the presigned upload).
            updated.state = { kind: "failed", retryable: false, reason };
            await this.persist(updated);
            return;
        }
        if (attempt >= backoff.maxAttempts) {
            // Transient in nature, but we've exhausted automatic attempts. Surface as
            // `retryable: true` so the UI can offer a manual "retry" action distinct from
            // a hard failure, without the driver continuing to burn backoff cycles on its own.
            updated.state = { kind: "failed", retryable: true, reason: `${reason} (max_attempts_exceeded)` };
            await this.persist(updated);
            return;
        }
        // Stay conceptually "in" the same stage (queued for part retries, completing for
        // complete-call retries) but record the failure reason transiently via state kind
        // unchanged; the FSM re-attempts the same step after the backoff delay.
        updated.state = item.state.kind === "completing" ? { kind: "completing" } : { kind: "queued" };
        await this.persist(updated);
        const delay = computeBackoffDelay(attempt, this.backoffOptions);
        this.schedule(id, delay);
    }
    // ── internals ──────────────────────────────────────────────────────────────────
    require(id) {
        const item = this.items.get(id);
        if (!item)
            throw new Error(`UploadQueue: unknown item id ${id}`);
        return item;
    }
    async transition(id, state, opts = {}) {
        const item = this.require(id);
        const updated = {
            ...item,
            state,
            attempt: opts.resetAttempt ? 0 : item.attempt,
            updatedAt: nowIso(this.clock),
        };
        await this.persist(updated);
        return updated;
    }
    async persist(item) {
        const existed = this.items.has(item.id);
        this.items.set(item.id, item);
        await this.storage.save(item);
        this.emit({ type: existed ? "item-updated" : "item-added", item });
    }
}
//# sourceMappingURL=queue.js.map